import React from 'react';
import { ArrowRightEndOnRectangleIcon } from '@heroicons/react/16/solid';
import { Navbar } from '../common/Catalyst/navbar';
import { Sidebar } from '../common/Catalyst/sidebar';
import { SidebarLayout } from '../common/Catalyst/sidebar-layout';
import { LOGO_URL } from './logo';
import { NavItem, NavSection } from './NavPrimitives';
import { SidebarFooter } from './SidebarFooter';

/**
 * The signed-in shell, before there is anyone signed in (#667).
 *
 * `/login` and `/callback` are the first screens a manager sees, so they
 * are built from the same pieces as every page after them rather than
 * styled to resemble them: Catalyst's `SidebarLayout` (the canvas, the
 * inset panel, the phone drawer), `AppSidebar`'s brand row and footer, and
 * a page's breadcrumb strip. Signing in then changes what is in the frame,
 * not the frame.
 *
 * The sidebar holds only what means something to someone not yet signed
 * in: the brand, where they are, and the theme switch. No company name or
 * user — there is none yet.
 */
const SignInSidebar: React.FC = () => (
  <Sidebar className="bg-canvas">
    {/* AppSidebar's top row, without the menu, search and new: none of
        them can do anything yet. Sized to that row's workspace button
        (34px tall, logo 7px in) so the brand and the first item land on
        the same pixels as on every signed-in page. */}
    <div className="px-3 pb-2 pt-3">
      <div className="flex h-[34px] items-center gap-2 px-[7px]">
        <img src={LOGO_URL} alt="" className="h-5 w-auto" />
        <span className="truncate text-[13px] font-semibold text-canvas-fg-strong">
          Epic HOAs
        </span>
      </div>
    </div>

    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-3 pb-3">
      <NavSection id="sign-in">
        <NavItem to="/login" icon={ArrowRightEndOnRectangleIcon} current>
          Sign in
        </NavItem>
      </NavSection>
    </div>

    {/* No one to show yet; the footer keeps the user row's place so the
        switch does not move when someone is. */}
    <SidebarFooter />
  </Sidebar>
);

export const SignInShell: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <SidebarLayout sidebar={<SignInSidebar />} navbar={<Navbar />}>
    {/* A page's breadcrumb strip, as on the detail pages. */}
    <div className="-mx-2 mb-6 flex items-center gap-2 border-b border-line/60 px-2 pb-3 text-[13px]">
      <span className="text-fg-muted">Epic HOAs</span>
      <span className="text-fg-faint" aria-hidden>
        /
      </span>
      <span className="font-medium text-fg">Sign in</span>
    </div>
    {children}
  </SidebarLayout>
);

export default SignInShell;
